/**
 * SHARED LICENSING API — one licence model, two products.
 * ──────────────────────────────────────────────────────
 * The Indie Publishing Roadmap and The Plot Planner are separate paid products
 * that share the SAME plumbing: Firebase Auth (Google-primary, licence-key
 * fallback), server-mediated licences (the `licenses` collection is denied to
 * clients — everything goes through these callables), and account-synced saving.
 *
 * A licence has a `scope`: 'roadmap' | 'planner' | 'bundle'. A bundle entitles
 * both tools. Legacy licences (minted before scopes existed) have no scope and
 * are treated as 'roadmap'. Author-plan SUB- keys are synthesised client-side
 * and treated as 'bundle'. Purchases are web-only (Stripe, one-off — never a
 * subscription); trials are a free 10 days, one per email per product.
 */
import { functions, auth, googleProvider } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { signInWithPopup } from 'firebase/auth';

/* ── Callables ─────────────────────────────────────────── */
export const validateLicenceFn    = httpsCallable(functions, 'validateLicence');
export const getMyLicenceFn        = httpsCallable(functions, 'getMyLicence');
export const getLicenceProgressFn  = httpsCallable(functions, 'getLicenceProgress');
export const saveLicenceProgressFn = httpsCallable(functions, 'saveLicenceProgress');
export const getPlannerProgressFn  = httpsCallable(functions, 'getPlannerProgress');
export const savePlannerProgressFn = httpsCallable(functions, 'savePlannerProgress');

// Checkout + trial callables, keyed by the product being purchased. The roadmap
// keeps its original two functions (unchanged contract); planner + bundle are new.
// ONE PLAN (Julie, 2026-07-29). The planner and bundle are no longer sold
// separately — everything is included in the single Narratoria plan — so the
// only checkout and trial that exist are the roadmap's. The planner/bundle
// callables remain deployed for now but are unreachable from the client, and
// their 6 Stripe price secrets stay permanently unused placeholders.
const CHECKOUT_FNS = {
  roadmap: httpsCallable(functions, 'createRoadmapCheckout'),
};
const TRIAL_FNS = {
  roadmap: httpsCallable(functions, 'startRoadmapTrial'),
};
export const checkoutFor = () => CHECKOUT_FNS.roadmap;
export const trialFor    = () => TRIAL_FNS.roadmap;

/* ── Product config ────────────────────────────────────── */
// One entry per ROUTE product (the two tools). `bundle` is a purchase option,
// not a route. cacheKey MUST differ per product so a cached roadmap licence and
// a cached planner licence never overwrite each other in localStorage. The
// roadmap keeps 'ncc_pub_licence' (also read by the /guides gate).
export const PRODUCT_CONFIG = {
  roadmap: {
    product: 'roadmap',
    cacheKey: 'ncc_pub_licence',
    homePath: '/publishing-roadmap',
    noun: 'Roadmap',            // "Unlock My Roadmap", "Choose your Roadmap plan"
    gateTitle: 'Indie Publishing',
    gateTitleAccent: 'Roadmap',
    signInHeading: 'Sign in to your Roadmap',
  },
  planner: {
    product: 'planner',
    cacheKey: 'ncc_planner_licence',
    homePath: '/plot-planner',
    noun: 'Planner',
    gateTitle: 'The Plot',
    gateTitleAccent: 'Planner',
    signInHeading: 'Sign in to your Planner',
  },
};
export const productConfig = (product) => PRODUCT_CONFIG[product] || PRODUCT_CONFIG.roadmap;

// Pricing (one-off, GBP). Roadmap prices are unchanged; planner matches; bundle
// is a small premium on the pair. Must mirror the Stripe prices Julie creates.
// PERMANENT (Julie, 2026-07-29). The Plot Planner is never sold as a standalone
// product: it ships as the Story Bible, included with the single Narratoria
// plan. This is no longer a dark-launch flag waiting to be flipped — it is the
// settled shape, and PricingPlans now filters planner/bundle unconditionally.
// Kept as a named constant so the intent stays readable at every call site.
export const PLANNER_DARK = true;

export const PRICING = {
  roadmap: {
    // ONE PLAN. The roadmap licence is the Narratoria plan and includes every
    // tool: the Publishing Checklist, Marketing Compass and the Story Bible.
    // Nothing is sold separately any more (Julie, 2026-07-29), so this copy
    // must list what's included rather than name a single product.
    label: 'the full toolkit',
    blurb: 'Everything included: the Publishing Checklist and guides, Marketing Compass, and the Story Bible.',
    plans: [
      { term: '1m',  qty: '1 month',   price: '£6.99',  note: 'Try the full toolkit' },
      { term: '6m',  qty: '6 months',  price: '£29.99', note: 'Most popular', featured: true },
      { term: '12m', qty: '12 months', price: '£44.99', note: 'Best value' },
    ],
  },
};

/* ── Entitlement ───────────────────────────────────────── */
const SCOPES = ['roadmap', 'planner', 'bundle'];
export const normaliseScope = (scope) => (SCOPES.includes(scope) ? scope : 'roadmap');
// Does a licence scope entitle a given route product? Bundle entitles both.
//
// ⚠️ DELIBERATELY UNCHANGED. This still means "does this scope match this
// product", and the guides gate, licence emails and renewal flows rely on that
// meaning. The one-plan-includes-everything rule lives in entitlesAnyTool below.
export function entitles(scope, product) {
  const s = normaliseScope(scope);
  return s === 'bundle' || s === product;
}

/**
 * ONE PLAN, ALL THREE TOOLS (Julie, 2026-07-29).
 *
 * Nothing is sold separately any more: the Publishing Checklist, Marketing
 * Compass and the Story Bible (the Plot Planner under its hub name) all come
 * with the single Narratoria plan. So ANY active scope entitles ANY tool —
 * roadmap, planner or bundle.
 *
 * `planner` is included explicitly and permanently so existing comp licences
 * keep working; they were minted before the plan was unified and must not be
 * stranded.
 *
 * This is a separate helper rather than a redefinition of `entitles` on purpose:
 * `entitles` still answers "does this scope match this product", which renewals,
 * licence emails and the /guides gate all depend on.
 */
export function entitlesAnyTool(scopeOrLicence) {
  const scope = typeof scopeOrLicence === 'string'
    ? scopeOrLicence
    : (scopeOrLicence && scopeOrLicence.scope);
  return SCOPES.includes(normaliseScope(scope));
}

/* ── Google sign-in helpers (product-aware) ────────────── */
// Fetch the signed-in user's licence for a product via getMyLicence. Returns an
// unlock payload { data, expired } (and caches it) or null. Only a licence that
// entitles this product is returned by the server.
export async function fetchMyLicenceUnlock(product) {
  const cfg = productConfig(product);
  const res = await getMyLicenceFn({ product: cfg.product });
  const lic = res.data;
  if (lic?.found && lic.licence?.key) {
    const data = {
      key: lic.licence.key,
      email: lic.licence.email,
      term: lic.licence.term || null,
      scope: normaliseScope(lic.licence.scope),
      expiresAtMs: lic.licence.expiresAtMs ?? null,
    };
    localStorage.setItem(cfg.cacheKey, JSON.stringify(data));
    return { data, expired: !!lic.expired };
  }
  return null;
}

/**
 * Where to send someone the MOMENT they finish signing in, at any door.
 *
 * Sign-in always lands on the hub (World 1's home) so every entry point —
 * the root, /marketing's locked state, and the tool licence gates — converges
 * on the same place. Superadmin is the standing exception.
 *
 * ⚠️ This is the SIGN-IN MOMENT ONLY. An already-signed-in member deep-linking
 * /publishing-roadmap must open the Roadmap directly, with no hub bounce — so
 * this is called from interactive sign-in handlers, never from the
 * onAuthStateChanged auto-unlock path.
 */
export function postSignInDestination(roles = []) {
  return roles.includes('superadmin') ? '/admin' : '/';
}

/** Roles from a Firebase ID-token claims object (array or legacy single). */
export function rolesFromClaims(claims = {}) {
  if (Array.isArray(claims.roles)) return claims.roles;
  if (claims.role) return [claims.role];
  return [];
}

/**
 * Finish an interactive sign-in by navigating to the hub (or /admin).
 * Returns true if it navigated, so callers can stop what they were doing.
 */
export async function goToPostSignInDestination(user) {
  try {
    const { getIdTokenResult } = await import('firebase/auth');
    const { claims } = await getIdTokenResult(user, true);
    window.location.href = postSignInDestination(rolesFromClaims(claims));
  } catch {
    window.location.href = '/';
  }
  return true;
}

// Google popup → licence lookup for a product. Resolves { unlocked:true, data,
// expired } when the account already has an entitling licence, else
// { unlocked:false, email } so the caller can pre-fill the trial. Throws on
// popup/auth failure (caller maps to a friendly message).
export async function googleSignInResult(product) {
  const { user } = await signInWithPopup(auth, googleProvider);
  const email = (user?.email || '').toLowerCase();
  const unlock = await fetchMyLicenceUnlock(product);
  // `user` is returned so callers can resolve roles for the post-sign-in landing.
  return unlock ? { unlocked: true, user, ...unlock } : { unlocked: false, user, email };
}

// Every message ends by pointing users to the licence-key path, so a sign-in
// hiccup never dead-ends a paying customer.
export function googleErrorMessage(err) {
  console.error('[licensing] Google sign-in failed:', err?.code, err?.message, err);
  const code = err?.code || '';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request')
    return 'Sign-in was cancelled. Try again, or unlock with your licence key below.';
  if (code === 'auth/popup-blocked')
    return 'Your browser blocked the sign-in popup. Allow popups and try again, or unlock with your licence key below.';
  if (code === 'auth/unauthorized-domain')
    return "Google sign-in isn't available on this address yet — please unlock with your licence key below.";
  return "Google sign-in didn't work just now. Try again, or unlock with your licence key below.";
}
